/**
 * EjecutarOrdenSheet
 * Vista simplificada para tecnicos al ejecutar un presupuesto asignado.
 * Flujo: detalle → firmas → ¿te pagó? → resumen
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { enviarOEncolar } from '../../utils/pendientesOffline';
import { useAuth } from '../../context/AuthContext';
import { generarRemitoPDFPremium } from '../../utils/generadorPdfRemito';
import { totalServicio } from '../../utils/descuento';
import { subirFotosEquipos, fotosPorSubir, precargarFotosEquipo } from '../../utils/subirFoto';
import { faltanFotos } from './FotosEquipo';
import PasoDetalle from './ejecutar/PasoDetalle';
import PasoFirmas from './ejecutar/PasoFirmas';
import PasoCobro from './ejecutar/PasoCobro';
import PasoResumenEjecutar from './ejecutar/PasoResumenEjecutar';

const PASOS = ['detalle', 'firmas', 'cobro', 'resumen'];
const fmt = v => `$${Math.round(Number(v) || 0).toLocaleString('es-AR')}`;

export default function EjecutarOrdenSheet({ servicio, onGuardado, onConfirmado, onCerrar }) {
    const { usuario } = useAuth();

    const [paso, setPaso] = useState('detalle');
    const [resumenGanancias, setResumenGanancias] = useState(null);
    const [observaciones, setObservaciones] = useState(servicio.observaciones || '');
    const [repuestosAgregados, setRepuestosAgregados] = useState([]);
    const [repuestosDisponibles, setRepuestosDisponibles] = useState([]);
    const [sheetRepuestosOpen, setSheetRepuestosOpen] = useState(false);
    // Fotos por equipo: hasta 4, con rótulo y sin obligación (18-oct-2026)
    const [fotosItems, setFotosItems] = useState(() => (servicio.items || []).map(it => ({
        fotoAntes: it.fotoAntes || null, fotoDespues: it.fotoDespues || null, fotosExtra: it.fotosExtra || [],
        fotosRotulos: it.fotosRotulos || null,
    })));
    const cambiarFotos = (i, c) => setFotosItems(fs => fs.map((f, k) => (k === i ? { ...f, ...c } : f)));
    // Los ítems sin equipo (mostrador, productos sueltos) no piden fotos
    const conEquipo = (it) => it.equipoSerial && !['MOSTRADOR', 'SIN-SN'].includes(String(it.equipoSerial).toUpperCase());
    const faltanPorItem = (servicio.items || []).map((it, i) => (conEquipo(it) ? faltanFotos(fotosItems[i]) : []));
    const [firmaTecnico, setFirmaTecnico] = useState(null);
    const [editandoFirma, setEditandoFirma] = useState(false);
    const [firmaCliente, setFirmaCliente] = useState(null);
    const [aclaracionCliente, setAclaracionCliente] = useState('');
    const [incluirFirmas, setIncluirFirmas] = useState(true);
    const [procesando, setProcesando] = useState(false);
    // "¿Te pagó?" (4-oct-2026): NO / EFECTIVO / TRANSFERENCIA. El precio sale del
    // presupuesto que armó el admin; el técnico ya no recalcula ni ajusta mano de obra.
    const [pago, setPago] = useState(null);
    const [monto, setMonto] = useState(0);
    const [descuentoEfectivo, setDescuentoEfectivo] = useState(0);

    useEffect(() => {
        try {
            const user = JSON.parse(localStorage.getItem('auth_usuario') || '{}');
            if (user.firma) setFirmaTecnico(user.firma);
            else setEditandoFirma(true);
        } catch {}
        api.get('/repuestos', { params: { size: 1000 } })
            .then(r => setRepuestosDisponibles(r.data?.content || r.data || []))
            .catch(() => {});
        api.get('/configuracion')
            .then(r => setDescuentoEfectivo(Number(r.data?.descuentoEfectivo) || 0))
            .catch(() => {});
    }, []);

    // Total = lo presupuestado (ítems, con descuento) + los repuestos que agregó en el lugar
    const repuestosNuevos = repuestosAgregados.reduce((s, r) => s + (parseFloat(r.precio) || 0) * (r.cantidad || 1), 0);
    // Lo presupuestado con su descuento lo calcula el backend (servicio.totales)
    const total = Math.round(totalServicio(servicio) + repuestosNuevos);
    // Productos a precio de venta (los pone el negocio): no entran en el reparto
    const totalProductos = (servicio.items || []).reduce((s, it) =>
        s + (it.repuestosUsados || []).reduce((a, r) => a + Number(r.subtotal ?? (Number(r.precio || 0) * Number(r.cantidad || 1))), 0), 0) + repuestosNuevos;

    const guardarFirma = async () => {
        if (!firmaTecnico) return;
        try {
            const user = JSON.parse(localStorage.getItem('auth_usuario') || '{}');
            localStorage.setItem('auth_usuario', JSON.stringify({ ...user, firma: firmaTecnico }));
            api.patch('/auth/mi-firma', { firma: firmaTecnico }).catch(() => {});
            setEditandoFirma(false);
        } catch {}
    };

    // 27-oct-2026: al pasar de las fotos a las firmas, las fotos se empiezan a subir en segundo plano;
    // al confirmar se reusan. Mientras guarda, el botón muestra en qué va.
    const [progreso, setProgreso] = useState(null);
    useEffect(() => {
        if (paso === 'firmas' || paso === 'cobro') fotosItems.forEach(f => precargarFotosEquipo(f));
    }, [paso]); // eslint-disable-line react-hooks/exhaustive-deps

    const confirmar = async () => {
        if (!usuario?.id) { toast.error('No se pudo identificar tu usuario. Cerra sesion y volve a entrar.'); return; }
        if (!pago) { toast.error('Decinos si te pagó'); return; }
        setProcesando(true);
        const totalFotos = fotosPorSubir(fotosItems);
        let hechas = 0;
        const pasoFotos = () => setProgreso({ texto: `Subiendo fotos ${hechas}/${totalFotos}`, pct: 8 + (hechas / totalFotos) * 57 });
        if (totalFotos > 0) pasoFotos(); else setProgreso({ texto: 'Guardando el trabajo…', pct: 35 });
        try {
            // Fotos de los equipos, en paralelo (las ya subidas en segundo plano se reusan); si alguna no sube, se suma después
            const { fotos: subidas, fallaron } = await subirFotosEquipos(fotosItems, (it, i) => `s${servicio.id}_${i}`, () => { hechas++; pasoFotos(); });
            setProgreso({ texto: 'Guardando el trabajo…', pct: 70 });
            if (fallaron) toast(`${fallaron} foto${fallaron !== 1 ? 's' : ''} no se pudo subir: sumala después desde la ficha del trabajo`, { icon: '📷', duration: 6000 });
            const itemsActualizados = (servicio.items || []).map((it, i) => ({
                equipoSerial: it.equipoSerial || 'MOSTRADOR',
                tecnico: it.tecnico || usuario?.nombre || 'Tecnico',
                costo: Number(it.costo || 0), costoExtra: Number(it.costoExtra || 0),
                metodoPago: it.metodoPago || 'EFECTIVO',
                trabajoRealizado: it.trabajoRealizado || '',
                trabajoTipo: it.trabajoTipo || 'REPARACION',
                garantiaHasta: it.garantiaHasta || null,
                fotoAntes: subidas[i]?.fotoAntes || null, fotoDespues: subidas[i]?.fotoDespues || null,
                fotosExtra: subidas[i]?.fotosExtra || [],
                fotosRotulos: subidas[i]?.fotosRotulos || null,
                repuestosUsados: i === 0
                    ? [...(it.repuestosUsados || []), ...repuestosAgregados]
                    : (it.repuestosUsados || []),
            }));
            // NO → queda COMPLETADO para que el admin defina el cobro.
            // EFECTIVO → COBRADO sin factura, el monto va a su cierre del día.
            // TRANSFERENCIA → COMPLETADO + nota, el admin confirma que entró la plata.
            const efectivo = pago === 'EFECTIVO';
            const nuevoEstado = efectivo ? 'COBRADO' : 'COMPLETADO';
            const notaTransf = pago === 'TRANSFERENCIA'
                ? `Pagó por transferencia ${fmt(monto)} — verificar` : '';
            const obsFinal = [observaciones, notaTransf].filter(Boolean).join('\n');
            const envio = await enviarOEncolar('put', `/servicios/${servicio.id}`, {
                sedeId: servicio.sedeId, usuarioId: usuario?.id || servicio.usuarioId,
                fecha: servicio.fecha, servicioTipo: servicio.servicioTipo || 'TECNICA',
                estado: nuevoEstado, clienteNombre: servicio.clienteNombre,
                sedeNombre: servicio.sedeNombre, descuentoPorcentaje: servicio.descuentoPorcentaje || 0,
                descuentoAlcance: servicio.descuentoAlcance || 'TOTAL',
                observaciones: obsFinal, items: itemsActualizados,
                // Firmas (16-oct-2026): quedan guardadas en el trabajo, no solo en el PDF
                ...(incluirFirmas ? { firmaCliente: firmaCliente || null, firmaTecnico: firmaTecnico || null, aclaracionCliente: firmaCliente ? aclaracionCliente.trim() : null } : {}),
                ...(efectivo ? { modalidadCobro: 'EFECTIVO_SIN_FACTURA', montoFinal: Number(monto) || total } : {}),
            }, `Trabajo ${servicio.clienteNombre || ''} #${servicio.id}`);
            if (envio.encolado) toast('Sin señal: el trabajo quedó guardado en el celular y se manda solo', { icon: '📶', duration: 6000 });
            else toast.success('Trabajo confirmado');

            setProgreso({ texto: 'Cerrando la visita…', pct: 82 });
            // La orden se completa apenas se guarda el trabajo (no al tocar "Listo")
            if (onGuardado) { try { await onGuardado(); } catch {} }
            setResumenGanancias({ pago, monto: Number(monto) || 0, total, totalProductos });

            try {
                const ticketItems = itemsActualizados.map(it => ({
                    ...it, totalCalculado: it.costo, trabajo: it.trabajoRealizado,
                }));
                setProgreso({ texto: 'Armando el PDF…', pct: 92 });
                await generarRemitoPDFPremium({
                    esPresupuesto: false, servicioId: servicio.id,
                    nroDocumentoExistente: servicio.nroDocumento || localStorage.getItem(`pdf_nro_${servicio.id}`) || null,
                    cliente: { nombre: servicio.clienteNombre, telefono: servicio.clienteTelefono, email: servicio.clienteEmail, cuilDni: servicio.clienteDni, condicionIva: servicio.clienteCondicionIva },
                    sede: { nombreSede: servicio.sedeNombre, direccion: servicio.sedeDireccion, piso: servicio.sedePiso, depto: servicio.sedeDepto },
                    tecnico: usuario?.nombre || localStorage.getItem('tecnico_nombre') || 'Tecnico',
                    ticketItems, fechaServicio: servicio.fecha,
                    descuentoPorcentaje: servicio.descuentoPorcentaje || 0, leyenda: obsFinal,
                    descuentoAlcance: servicio.descuentoAlcance || 'TOTAL',
                    // Totales del backend + los repuestos que sumó en el lugar (van sin descuento)
                    totales: servicio.totales ? {
                        ...servicio.totales,
                        subtotal: Number(servicio.totales.subtotal) + repuestosNuevos,
                        total:    Number(servicio.totales.total) + repuestosNuevos,
                    } : null,
                    esTecnicoForzado: true, fotosTrabajo: servicio.fotosTrabajo || [],
                    firmaTecnico: incluirFirmas ? (firmaTecnico || null) : null,
                    firmaCliente: incluirFirmas ? (firmaCliente || null) : null, incluirFirmas,
                    aclaracionCliente: incluirFirmas && firmaCliente ? aclaracionCliente.trim() : '',
                });
            } catch (pdfErr) {
                console.warn('PDF no generado:', pdfErr);
                toast('Trabajo guardado. PDF no disponible', { icon: '⚠️' });
            }
            setPaso('resumen');
        } catch (e) {
            const detalle = e?.response?.data?.mensaje || e?.response?.data?.message || e?.message || '';
            toast.error(`Error al confirmar${detalle ? ': ' + detalle : ''}`);
        } finally { setProcesando(false); setProgreso(null); }
    };

    return (
        <div className="fixed inset-0 z-[2000] flex flex-col bg-page">
            {/* Header */}
            <div className="shrink-0 px-4 pt-4 pb-3 bg-panel border-b border-black/[0.08]">
                <div className="flex items-center gap-3">
                    <button onClick={onCerrar}
                        className="w-9 h-9 rounded-xl flex items-center justify-center font-bold bg-chip text-secondary active:scale-90">
                        ←
                    </button>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-title font-black text-ink leading-none">Cerrar trabajo</h2>
                        <p className="text-caption text-muted truncate mt-0.5">{servicio.clienteNombre} · {servicio.sedeNombre}</p>
                    </div>
                    <div className="text-right shrink-0">
                        <p className="text-label font-black text-muted uppercase tracking-wider">Total</p>
                        <p className="text-title font-black leading-none text-ink">{fmt(total)}</p>
                    </div>
                </div>
                <div className="flex gap-1 mt-3">
                    {PASOS.slice(0, 3).map((s, i) => (
                        <div key={s} className={`flex-1 h-1 rounded-full transition-colors ${i <= PASOS.indexOf(paso) ? 'bg-brand-red' : 'bg-chip'}`} />
                    ))}
                </div>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 pb-8">
                {paso === 'detalle' && (
                    <PasoDetalle
                        servicio={servicio} observaciones={observaciones} setObservaciones={setObservaciones}
                        repuestosAgregados={repuestosAgregados} setRepuestosAgregados={setRepuestosAgregados}
                        repuestosDisponibles={repuestosDisponibles}
                        sheetRepuestosOpen={sheetRepuestosOpen} setSheetRepuestosOpen={setSheetRepuestosOpen}
                        fotosItems={fotosItems} cambiarFotos={cambiarFotos} faltanPorItem={faltanPorItem}
                        onNext={() => {
                            const i = faltanPorItem.findIndex(f => f.length);
                            if (i >= 0) { toast.error(`${servicio.items[i].equipoSerial}: falta la ${faltanPorItem[i].join(' y la ')}`); return; }
                            setPaso('firmas');
                        }}
                    />
                )}
                {paso === 'firmas' && (
                    <PasoFirmas
                        firmaTecnico={firmaTecnico} setFirmaTecnico={setFirmaTecnico}
                        editandoFirma={editandoFirma} setEditandoFirma={setEditandoFirma}
                        firmaCliente={firmaCliente} setFirmaCliente={setFirmaCliente}
                        aclaracionCliente={aclaracionCliente} setAclaracionCliente={setAclaracionCliente}
                        incluirFirmas={incluirFirmas} setIncluirFirmas={setIncluirFirmas}
                        guardarFirma={guardarFirma}
                        onBack={() => setPaso('detalle')} onNext={() => setPaso('cobro')}
                    />
                )}
                {paso === 'cobro' && (
                    <PasoCobro
                        total={total} descuentoEfectivo={descuentoEfectivo}
                        pago={pago} setPago={setPago} monto={monto} setMonto={setMonto}
                        procesando={procesando} progreso={progreso}
                        onBack={() => setPaso('firmas')} onConfirmar={confirmar}
                    />
                )}
                {paso === 'resumen' && resumenGanancias && (
                    <PasoResumenEjecutar resumenGanancias={resumenGanancias} onConfirmado={onConfirmado} />
                )}
            </div>
        </div>
    );
}
